import React, {useRef, useContext, useMemo} from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import SMCField from '../SMCField';
import clsx from 'clsx';
import RadioGroupContext from './RadioGroupContext';
import {hasValue} from '../../../utils/utils';
import {useSMCDisabledContext} from '../SMCDisabledContext';

/**
 * 
 * @param {object} params
 * @param {boolean} [params.disabled]
 * @param {function} [params.onChange]
 * @param {boolean} [params.labelClickable]
 * @param {(string|number)} [params.height]
 * @param {(string|number)} [params.width]
 * @param {boolean} [params.checked]
 * @param {string} [params.name]
 * @param {string} [params.value]
 * @param {string} [params.label]
 * @param {('start'|'end')} [params.labelPosition]
 * @param {(string|number)} [params.labelWidth]
 * @param {string} [params.tooltip]
 * @param {boolean} [params.inline]
 * @param {*} [params.innerRef]
 * @param {string} [params.className]
 * @param {boolean} [params.ignoreDisabledContext]
 */
function SMCRadio({
    disabled,
    onChange,
    labelClickable=true,
    checked,
    height,
    name,
    value,
    label,
    labelPosition="end",
    labelWidth,
    tooltip,
    inline,
    innerRef,
    className,
    ignoreDisabledContext,
    style
}) {
    const classes = useStyles();
    const radioGroupContext = useContext(RadioGroupContext);
    const disabledContext = useSMCDisabledContext();
    
    const ref = useRef();
    const radioRef = innerRef || ref;

    const radioSelectedValue = (radioGroupContext && hasValue(radioGroupContext.selectedValue)) ? radioGroupContext.selectedValue : undefined;
    const radioOnChange      = (radioGroupContext && hasValue(radioGroupContext.onChange)) ? radioGroupContext.onChange : onChange;
    const radioDisabled      = (radioGroupContext && hasValue(radioGroupContext.disabled)) ? radioGroupContext.disabled : disabled;
    const radioName          = (radioGroupContext && hasValue(radioGroupContext.name)) ? radioGroupContext.name : name;
    
    const radioChecked = hasValue(radioSelectedValue) ? value === radioSelectedValue : checked;

    const _disabled = useMemo(() => {
        if (disabledContext && !ignoreDisabledContext) {
            if (disabledContext.forceDisable) {
                return true;
            }
            if (disabledContext.forceEnable) {
                return false;
            }
        }
        if (hasValue(radioDisabled)) {
            return radioDisabled;
        }
        return undefined;
    }, [radioDisabled, disabledContext, ignoreDisabledContext]);
    
    const onLabelClick = () => {
        if (labelClickable) {
            radioRef.current.focus();
            radioRef.current.click();
        }
    }


    const radioElem = (
        <input
            type="radio"
            value={value}
            checked={radioChecked}
            onChange={radioOnChange}
            disabled={_disabled}
            name={radioName}
            ref={radioRef}
            className={clsx(className, classes.root)}
            style={style}
        />
    );

    return (
        <SMCField
            disabled={_disabled}
            height={height}
            inline={inline}
            label={label}
            // Unless explicitly stated that label position is start, place it at end
            labelPosition={labelPosition === 'start' ? 'start' : 'end'}
            labelWidth={labelWidth}
            tooltip={tooltip}
            labelOnClick={labelClickable ? onLabelClick : undefined}
        >
            {radioElem}
        </SMCField>
    );
}

const useStyles = makeStyles({
    root: {
        position: 'relative',
        cursor: 'pointer',
        marginLeft: '0px',
        marginRight: '7px',
        verticalAlign: 'middle',
        marginTop: '-1px',
        '&:disabled': {
            cursor: 'default'
        },
        '&::before': {
            height: '12px',
            width: '12px',
            backgroundColor: '#ffffff',
            content: '""',
            display: 'inline-block',
            verticalAlign: 'baseline',
            transition: '0.3s',
            border: '1px solid #D2D5D9',
            borderRadius: '50%'        
        },
        '&:hover:before': {
            border: '1px solid #4FB4F1'
        },
        '&:active:before': {
            backgroundColor: '#cccdcf',
            border: '1px solid #000'
        },
        '&:disabled:before': {
            border: '1px solid #E7E9EB'
        },
        /* The radio button dot */
        '&:checked:after': {
            backgroundColor: '#2E3F58',
            content: '""',
            display: 'inline-block',
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            position: 'absolute',
            top: '4px',
            left: '4px'
        },
        /* Disabled radio button dot */
        '&:checked:disabled:after': {
            backgroundColor: '#a7a9ac',
        }
    }
})

SMCRadio.propTypes = {
    disabled: PropTypes.bool,
    onChange: PropTypes.func,
    checked: PropTypes.bool,
    name: PropTypes.string,
    value: PropTypes.string,
    label: PropTypes.node,
    tooltip: PropTypes.node
}

export default React.memo(SMCRadio);